import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Compass, Navigation2 } from "lucide-react";

export default function CompassToTarget({ lat, lng, stageName }) {
  const { t, i18n } = useTranslation();
  const [userPos, setUserPos] = useState(null);
  const [heading, setHeading] = useState(null); // device-facing direction in degrees (0 = North)
  const [error, setError] = useState("");
  const [enabled, setEnabled] = useState(false);
  const watchRef = useRef(null);
  const orientListenerRef = useRef(null);

  useEffect(() => () => {
    if (watchRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchRef.current);
    }
    if (orientListenerRef.current) {
      window.removeEventListener("deviceorientationabsolute", orientListenerRef.current);
      window.removeEventListener("deviceorientation", orientListenerRef.current);
    }
  }, []);

  const hasTarget = typeof lat === "number" && typeof lng === "number";

  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;

  const distanceKm = userPos && hasTarget
    ? haversine(userPos.lat, userPos.lng, lat, lng)
    : null;

  const bearing = userPos && hasTarget ? computeBearing(userPos.lat, userPos.lng, lat, lng) : null;
  const relative = bearing != null && heading != null ? (bearing - heading + 360) % 360 : bearing;

  const enable = async () => {
    setError("");
    if (!hasTarget) {
      setError(t("profile.compass_no_target"));
      return;
    }
    if (!navigator.geolocation) {
      setError(t("profile.compass_no_gps"));
      return;
    }

    // Ask device orientation permission (iOS needs explicit prompt)
    try {
      if (typeof window.DeviceOrientationEvent !== "undefined" &&
          typeof window.DeviceOrientationEvent.requestPermission === "function") {
        const res = await window.DeviceOrientationEvent.requestPermission();
        if (res !== "granted") {
          // still continue — bearing without heading will point toward absolute North
        }
      }
    } catch (_) { /* ignore */ }

    const onOrient = (e) => {
      // webkitCompassHeading = iOS; alpha = others (inverse)
      const h = e.webkitCompassHeading != null ? e.webkitCompassHeading : (e.alpha != null ? 360 - e.alpha : null);
      if (h != null && !Number.isNaN(h)) setHeading(h);
    };
    orientListenerRef.current = onOrient;
    window.addEventListener("deviceorientationabsolute", onOrient, true);
    window.addEventListener("deviceorientation", onOrient, true);

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setUserPos({ lat: coords.latitude, lng: coords.longitude });
        setEnabled(true);
        watchRef.current = navigator.geolocation.watchPosition(
          ({ coords: c }) => setUserPos({ lat: c.latitude, lng: c.longitude }),
          () => {},
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
        );
      },
      (err) => setError(t("profile.compass_loc_error", { err: err.message })),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (!hasTarget) return null;

  return (
    <div
      className="p-4"
      style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
      data-testid="profile-compass"
    >
      {!enabled ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="overline mb-1" style={{ color: "var(--brand-primary)" }}>{t("profile.compass_title")}</div>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }} dangerouslySetInnerHTML={{ __html: t("profile.compass_intro", { name: stageName || "" }) }} />
          </div>
          <button
            onClick={enable}
            className="btn-primary text-xs inline-flex items-center gap-2"
            data-testid="compass-enable-btn"
          >
            <Compass size={14} strokeWidth={1.4} /> {t("profile.compass_guide_me")}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-4" data-testid="compass-active">
          <div
            className="relative flex items-center justify-center"
            style={{
              width: 80, height: 80,
              borderRadius: "50%",
              background: "radial-gradient(circle, #1F1F1F 0%, #0A0A0A 100%)",
              border: "1px solid var(--brand-primary)",
              boxShadow: "0 0 20px rgba(200,16,46,0.2) inset",
            }}
          >
            <div
              style={{
                transform: `rotate(${relative ?? 0}deg)`,
                transition: "transform 0.3s ease-out",
                color: "var(--brand-primary)",
                filter: "drop-shadow(0 0 6px rgba(200,16,46,0.7))",
              }}
            >
              <Navigation2 size={44} strokeWidth={1.4} fill="var(--brand-primary)" />
            </div>
            {/* N marker */}
            <span
              className="absolute text-[10px] uppercase tracking-widest"
              style={{ top: 4, color: "var(--vip-gold)" }}
            >
              N
            </span>
          </div>
          <div>
            <div className="overline mb-1" style={{ color: "var(--brand-primary)" }}>
              {t("profile.compass_toward", { name: stageName || "" })}
            </div>
            <div className="font-serif text-2xl" style={{ color: "var(--text-primary)" }} data-testid="compass-distance">
              {distanceKm != null ? `${distanceKm.toFixed(1)} km` : "—"}
            </div>
            {bearing != null && (
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }} data-testid="compass-bearing">
                {t("profile.compass_bearing", { deg: Math.round(bearing), card: cardinal(bearing, i18n.language) })}
                {heading != null && " " + t("profile.compass_with_heading", { deg: Math.round(heading) })}
              </p>
            )}
          </div>
        </div>
      )}
      {error && (
        <p className="text-xs mt-3" style={{ color: "#ef6d6d" }} data-testid="compass-error">{error}</p>
      )}
    </div>
  );
}

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function computeBearing(lat1, lon1, lat2, lon2) {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function cardinal(deg, lang) {
  const dirsEs = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
  const dirsEn = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const dirs = (lang || "").startsWith("en") ? dirsEn : dirsEs;
  return dirs[Math.round(deg / 45) % 8];
}
